// Types for Mobile Bond Insurance Analysis Flow

export type TenantType = 'NATURAL' | 'LEGAL';
export type PropertyPurpose = 'RESIDENTIAL' | 'COMMERCIAL';
export type PoliticallyExposedPersonType = 'YES' | 'NO' | 'CLOSE_TO';

export interface SympathiseTenant {
  cpf: string;
  name: string;
  gender: string;
  birthDate: string;
  motherName: string;
  email: string;
  phone: string;
  maritalStatus: string;
  nationality: string;
  resident: boolean;
  politicallyExposedPerson: PoliticallyExposedPersonType;
  pepName: string;
  pepCpf: string;
  pepKinship: string;
  employmentRelationship: string;
  profession: string;
  monthlyFixedIncome: string;
  otherIncomes: string;
}

export interface Partner {
  cpf: string;
  name: string;
  percentCapital: string;
  dateOfEntry: string;
}

export interface MobileAnalysisFormData {
  // Step 1: Type Selection
  estateId: string;
  estateName: string;
  purpose: PropertyPurpose;
  tenantType: TenantType;

  // Step 2: Tenant Data (Natural Person)
  naturalTenant: {
    cpf: string;
    name: string;
    email: string;
    phone: string;
    birthDate: string;
    gender: string;
    motherName: string;
    nationality: string;
    maritalStatus: string;
    hasSocialName: boolean;
    socialName: string;
    politicallyExposedPerson: PoliticallyExposedPersonType;
    pepName: string;
    pepCpf: string;
    pepKinship: string;
    employmentRelationship: string;
    employmentAdmissionDate?: string;
    profession: string;
    // Resident section
    isResident: boolean;
    residentCpf: string;
    residentName: string;
    residentGender: string;
    residentBirthDate: string;
    residentMotherName: string;
    residentEmail: string;
    residentPhone: string;
    residentMaritalStatus: string;
    residentNationality: string;
    residentPoliticallyExposedPerson: PoliticallyExposedPersonType;
    residentPepName: string;
    residentPepCpf: string;
    residentPepKinship: string;
    residentEmploymentRelationship: string;
    residentProfession: string;
    // Marital partner (conditional on married/union)
    partnerCpf: string;
    partnerName: string;
    // Sympathise tenants
    includeSympathiseTenants: boolean;
    sympathiseTenants: SympathiseTenant[];
  };

  // Step 2: Tenant Data (Legal Person)
  legalTenant: {
    cnpj: string;
    companyName: string;
    foundationDate: string;
    lastContractualAlterationDate: string;
    shareCapital: string;
    paidInCapital: string;
    anualRevenue: string;
    email: string;
    phone: string;
    contactName: string;
    contactPhone: string;
    partners: Partner[];
  };

  // Step 3: Property Address
  property: {
    zipCode: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    propertyType: string;
    reason: string;
  };

  // Step 4: Values
  values: {
    rentValue: string;
    condominiumFee: string;
    isCondominiumEnabled: boolean;
    taxesValue: string;
    isTaxesEnabled: boolean;
    energyBill: string;
    isEnergyEnabled: boolean;
    waterBill: string;
    isWaterEnabled: boolean;
    gasBill: string;
    isGasEnabled: boolean;
    validity: number;
  };

}

export interface StepProps {
  formData: MobileAnalysisFormData;
  updateFormData: (data: Partial<MobileAnalysisFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export interface EstateOption {
  id: string;
  name: string;
  cnpj?: string;
}

const emptySympathiseTenant: SympathiseTenant = {
  cpf: '',
  name: '',
  gender: '',
  birthDate: '',
  motherName: '',
  email: '',
  phone: '',
  maritalStatus: '',
  nationality: 'Brasileira',
  resident: false,
  politicallyExposedPerson: 'NO',
  pepName: '',
  pepCpf: '',
  pepKinship: '',
  employmentRelationship: '',
  profession: '',
  monthlyFixedIncome: '',
  otherIncomes: '',
};

const emptyPartner: Partner = {
  cpf: '',
  name: '',
  percentCapital: '',
  dateOfEntry: '',
};

export { emptySympathiseTenant, emptyPartner };

export const initialFormData: MobileAnalysisFormData = {
  estateId: '',
  estateName: '',
  purpose: 'RESIDENTIAL',
  tenantType: 'NATURAL',

  naturalTenant: {
    cpf: '',
    name: '',
    email: '',
    phone: '',
    birthDate: '',
    gender: '',
    motherName: '',
    nationality: 'Brasileira',
    maritalStatus: '',
    hasSocialName: false,
    socialName: '',
    politicallyExposedPerson: 'NO',
    pepName: '',
    pepCpf: '',
    pepKinship: '',
    employmentRelationship: '',
    employmentAdmissionDate: '',
    profession: '',
    isResident: true,
    residentCpf: '',
    residentName: '',
    residentGender: '',
    residentBirthDate: '',
    residentMotherName: '',
    residentEmail: '',
    residentPhone: '',
    residentMaritalStatus: '',
    residentNationality: 'Brasileira',
    residentPoliticallyExposedPerson: 'NO',
    residentPepName: '',
    residentPepCpf: '',
    residentPepKinship: '',
    residentEmploymentRelationship: '',
    residentProfession: '',
    partnerCpf: '',
    partnerName: '',
    includeSympathiseTenants: false,
    sympathiseTenants: [{ ...emptySympathiseTenant }],
  },

  legalTenant: {
    cnpj: '',
    companyName: '',
    foundationDate: '',
    lastContractualAlterationDate: '',
    shareCapital: '',
    paidInCapital: '',
    anualRevenue: '',
    email: '',
    phone: '',
    contactName: '',
    contactPhone: '',
    partners: [{ ...emptyPartner }],
  },

  property: {
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
    propertyType: '',
    reason: '',
  },

  values: {
    rentValue: '',
    condominiumFee: '',
    isCondominiumEnabled: false,
    taxesValue: '',
    isTaxesEnabled: false,
    energyBill: '',
    isEnergyEnabled: false,
    waterBill: '',
    isWaterEnabled: false,
    gasBill: '',
    isGasEnabled: false,
    validity: 12,
  },

};
